import React from 'react'
import AntdCheckableTag from 'antd/es/tag/CheckableTag'
import type { CheckableTagProps } from 'antd/es/tag/CheckableTag'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import type { TagProps } from './props'
import { Tag as AntdSeamTag } from './Tag.antd'
import {
  Tag as ShadcnSeamTag,
  CheckableTag as ShadcnCheckableTag,
} from './Tag.shadcn'
import { PRESET_COLORS } from './utils'

export { PRESET_COLORS }
export type { TagProps }
export type { CheckableTagProps }

/**
 * Compat seam for antd's Tag — and the FOURTH component put behind the design
 * toggle, after `../Button` (#970) and `../Typography` + `../Space` (#971).
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders
 * `./Tag.antd` with the props it was given: same element, same class names,
 * same DOM. The only difference from the previous `./index.tsx` — which IS
 * `./Tag.antd`, moved — is one extra component in the React tree, which
 * renders nothing of its own. `../__tests__/tag-seam-parity.test.tsx` asserts
 * that byte for byte across a prop matrix, and it is the test to keep green:
 * `development` continuing to ship today's rendering unchanged to customers is
 * the whole premise of shipping shadcn one component at a time.
 *
 * READ `./Tag.antd` BEFORE ANYTHING ELSE HERE. Tag is the first ported seam
 * whose antd branch is not antd: `src/antd` has always SHADOWED Tag rather
 * than re-exported it (one of the six — see AGENTS.md), replacing antd's tag
 * palette with this repo's `bold`/`subtle`/`minimal` × `TagColorType` system.
 * "Unchanged" therefore means unchanged versus that seam, not versus
 * `antd/es/tag`, and the parity test compares against `./Tag.antd` for exactly
 * that reason. The 14 files still importing `Tag` from raw `antd` are the
 * documented deferred residual; the toggle does not reach them, by design.
 *
 * WHY TAG WAS CHOSEN FOURTH. Reach, and a palette worth looking at. 322 files
 * import it from the seam and 859 JSX tags render it — the widest surface left
 * after Typography and Space — and unlike those two it is a COLOUR-carrying
 * component, so flipping the control is a real test of whether the shadcn
 * foundation can host this repo's own palette rather than only its layout. It
 * carries no popup and no portal. The reference branch's visual-defect survey
 * records no open defect against it.
 *
 * `CheckableTag` dispatches on the mode exactly as `Tag` does, so the seam
 * cannot end up half-switched — it is exposed BOTH as a static on `Tag` and as
 * a named barrel export, and both must move together.
 * ------------------------------------------------------------------------- */
const TagDispatcher = (props: TagProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamTag {...props} />

  return <AntdSeamTag {...props} />
}

TagDispatcher.displayName = 'Tag'

const CheckableTagDispatcher = React.forwardRef<
  HTMLSpanElement,
  CheckableTagProps
>((props, ref) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') {
    // antd's `CheckableTagProps` carries `prefixCls`, which the transcribed
    // shadcn component has no use for and must not leak to the DOM.
    const { prefixCls: _prefixCls, ...rest } = props
    return <ShadcnCheckableTag {...rest} ref={ref} />
  }

  return <AntdCheckableTag {...props} ref={ref} />
})

CheckableTagDispatcher.displayName = 'Tag.CheckableTag'

export const CheckableTag = CheckableTagDispatcher

export const Tag = Object.assign(TagDispatcher, {
  CheckableTag: CheckableTagDispatcher,
})
